import { Show } from "solid-js";
import { A } from "@solidjs/router";
import PrivacySettingsLink from "@/tracking/components/PrivacySettingsLink";
import { themeState } from "@/shared/stores/themeStore";
import { t } from "@/shared/i18n";

const LINK =
  "inline-flex items-center rounded py-1.5 text-xs text-nav-muted underline underline-offset-4 " +
  "transition-colors hover:text-nav focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:outline-offset-2 focus-visible:outline-white";

/** Contact details sit on the same line but are not links to a policy, so they are not underlined. */
const CONTACT = "text-nav-muted transition-colors hover:text-nav";

/**
 * The company whose site this is.
 *
 * <p>The tracking section's <code>controllerName</code> first, because that is the field a tenant
 * already fills in with the legal entity behind the site — the same one the privacy statement names
 * as the data controller. A footer and a privacy page disagreeing about who runs the page is worse
 * than either being slightly wrong. Falls back to the display name, which every tenant has.</p>
 */
const company = () =>
  themeState.tenant.tracking?.controllerName?.trim() || themeState.tenant.name;

/**
 * Where this tenant says its privacy statement lives.
 *
 * <p>A partner's is usually their own page on their own domain, which is the whole point of the
 * setting: a visitor reading what is done with their data should be reading the statement of the
 * company they think they are dealing with.</p>
 */
const privacyHref = () => themeState.tenant.tracking?.privacyPolicyUrl || "/personvern";

/** Whether that url leaves this app, and therefore cannot be a client-side route. */
const isExternal = () => /^https?:\/\//i.test(privacyHref());

/**
 * The one strip that is on every page: who runs the site, how to reach them, and the way back into
 * the consent choices.
 *
 * <p>Withdrawing consent has to be as easy as giving it, and the banner disappears the moment a
 * decision exists. Mounted in the router's layout rather than on individual pages so there is no
 * route — least of all the configurator, where visitors spend the most time — from which the only
 * remaining route is clearing site data.</p>
 *
 * <p>The contact details live here rather than on the entrance page. Two footers stacked a line apart
 * is what a second strip actually looks like, and on a short viewport the lower one — the one with the
 * consent controls — falls below the fold. One line carries both.</p>
 *
 * <p>Deliberately quiet: small, muted, and after the page content rather than fixed over it. It is a
 * door that must always be there, not one that asks for attention.</p>
 */
export default function SiteFooter() {
  return (
    <footer
      class="mt-auto border-t border-white/5 bg-nav px-6 py-4 backdrop-blur-xl"
      // The consent choices live here; naming the region lets a screen reader jump to it.
      aria-label={t("consent.footerLabel", "Personvern")}
    >
      <div class="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-nav-faint">
        {/* Each part appears only if this tenant has it: a partner who has not given us a phone
            number shows no phone rather than ours, and the separator is emitted with the part it
            belongs to so a missing one cannot leave a stranded dot. */}
        <span>
          {company()}
          <Show when={themeState.tenant.contactPhone}>
            {(phone) => (
              <>
                &nbsp;·&nbsp;{" "}
                <a href={`tel:${phone().replace(/\s+/g, "")}`} class={CONTACT}>
                  {phone()}
                </a>
              </>
            )}
          </Show>
          <Show when={themeState.tenant.contactEmail}>
            {(email) => (
              <>
                &nbsp;·&nbsp;{" "}
                <a href={`mailto:${email()}`} class={CONTACT}>
                  {email()}
                </a>
              </>
            )}
          </Show>
        </span>

        {/* A router link only when the url is one this app can route. Handing <A> an absolute url
            makes it a path, and the visitor lands on a 404 inside our own app instead of on the
            partner's page. */}
        <Show
          when={isExternal()}
          fallback={
            <A href={privacyHref()} class={LINK}>
              {t("consent.privacyLink", "Personvernerklæring")}
            </A>
          }
        >
          <a href={privacyHref()} rel="noreferrer" class={LINK}>
            {t("consent.privacyLink", "Personvernerklæring")}
          </a>
        </Show>

        <PrivacySettingsLink class="text-nav-muted hover:text-nav" />
      </div>
    </footer>
  );
}
